// Team Capability — the score at the top of the admin panel. The rules come
// from Ronan's Capability mode design (its roll-ups):
//  - The unit is one person x one capability of their role (a "pair").
//  - Each pair has a level: Weak, Developing, Capable or Advanced, set by the
//    latest scored Roleplay or quiz covering that capability. The bar is Capable.
//  - Score = pairs at Capable or better / pairs assessed. A pair that hasn't
//    been assessed is left out, never counted as a zero.
//  - Never shown without the weakest capability across the same people.

export type CapabilityLevel = 'weak' | 'developing' | 'capable' | 'advanced';

export interface CapabilityPair {
  personId: string;
  teamId: string;
  /** Everyone in a role shares its capabilities, so per role, scores = employees x capabilities. */
  roleId: string;
  roleName: string;
  capabilityId: string;
  capabilityName: string;
  /** Where the capability comes from: a role's own (absent), or a shared Development or Standards one. */
  source?: string;
  /** null = not assessed yet. */
  level: CapabilityLevel | null;
}

export interface Ratio {
  count: number;
  total: number;
  /** Whole percent, or null when nothing has been assessed. */
  percent: number | null;
}

export const ratio = (count: number, total: number): Ratio => ({
  count,
  total,
  percent: total === 0 ? null : Math.round((count / total) * 100)
});

export const meetsBar = (level: CapabilityLevel | null) => level === 'capable' || level === 'advanced';

/** Pairs at Capable or better, over pairs assessed. */
export const capabilityScore = (pairs: CapabilityPair[]): Ratio => {
  const assessed = pairs.filter((p) => p.level !== null);
  return ratio(assessed.filter((p) => meetsBar(p.level)).length, assessed.length);
};

/** The capability with the lowest share at Capable or better. Ties go to the first seen. */
export const weakestCapability = (
  pairs: CapabilityPair[]
): { capabilityId: string; name: string; ratio: Ratio } | null => {
  const byCapability = new Map<string, CapabilityPair[]>();
  for (const pair of pairs) {
    const list = byCapability.get(pair.capabilityId);
    if (list) list.push(pair);
    else byCapability.set(pair.capabilityId, [pair]);
  }
  const scored = Array.from(byCapability.values())
    .map((list) => ({ capabilityId: list[0].capabilityId, name: list[0].capabilityName, ratio: capabilityScore(list) }))
    .filter((c) => c.ratio.percent !== null);
  if (scored.length === 0) return null;
  return scored.reduce((weakest, c) => ((c.ratio.percent as number) < (weakest.ratio.percent as number) ? c : weakest));
};

export interface ScoreCounts {
  /** Distinct employees with at least one capability, assessed or not. */
  employees: number;
  /** Distinct capabilities across their roles. Shared ones count once. */
  capabilities: number;
  /** Every employee x capability score, assessed or not. */
  scores: number;
  /** Scores not assessed yet: left out of the percentage. */
  notAssessed: number;
  /** Employees with no assessed score at all. */
  employeesNotAssessed: number;
}

/** The counts behind the score line: "30 employees and 24 capabilities, for 200 capability scores". */
export const scoreCounts = (pairs: CapabilityPair[]): ScoreCounts => {
  const assessedPeople = new Set(pairs.filter((p) => p.level !== null).map((p) => p.personId));
  const people = new Set(pairs.map((p) => p.personId));
  return {
    employees: people.size,
    capabilities: new Set(pairs.map((p) => p.capabilityId)).size,
    scores: pairs.length,
    notAssessed: pairs.filter((p) => p.level === null).length,
    employeesNotAssessed: Array.from(people).filter((id) => !assessedPeople.has(id)).length
  };
};

export interface RoleLine {
  teamId: string;
  roleId: string;
  roleName: string;
  employees: number;
  capabilities: number;
  /** employees x capabilities: everyone in a role has the same capabilities. */
  scores: number;
}

/** One multiplication per team and role, in first-seen order: the lines the Breakdown adds up. */
export const roleBreakdown = (pairs: CapabilityPair[]): RoleLine[] => {
  const lines = new Map<string, { line: RoleLine; people: Set<string>; caps: Set<string> }>();
  for (const pair of pairs) {
    const key = `${pair.teamId}|${pair.roleId}`;
    let entry = lines.get(key);
    if (!entry) {
      entry = {
        line: {
          teamId: pair.teamId,
          roleId: pair.roleId,
          roleName: pair.roleName,
          employees: 0,
          capabilities: 0,
          scores: 0
        },
        people: new Set(),
        caps: new Set()
      };
      lines.set(key, entry);
    }
    entry.people.add(pair.personId);
    entry.caps.add(pair.capabilityId);
    entry.line.scores += 1;
  }
  return Array.from(lines.values()).map(({ line, people, caps }) => ({
    ...line,
    employees: people.size,
    capabilities: caps.size
  }));
};

// Capability % colour bands: red to 24, orange to 50, yellow to 74, green to 100.
export const CAPABILITY_COLORS = { red: '#FF6A55', orange: '#F79420', yellow: '#FACC15', green: '#22C55E' };

export const capabilityColor = (percent: number | null): string => {
  if (percent === null) return '#FDFDFD';
  if (percent <= 24) return CAPABILITY_COLORS.red;
  if (percent <= 50) return CAPABILITY_COLORS.orange;
  if (percent <= 74) return CAPABILITY_COLORS.yellow;
  return CAPABILITY_COLORS.green;
};

/** The capability with the highest share at Capable or better. Ties go to the first seen. */
export const strongestCapability = (
  pairs: CapabilityPair[]
): { capabilityId: string; name: string; ratio: Ratio } | null => {
  const byCapability = new Map<string, CapabilityPair[]>();
  for (const pair of pairs) {
    const list = byCapability.get(pair.capabilityId);
    if (list) list.push(pair);
    else byCapability.set(pair.capabilityId, [pair]);
  }
  const scored = Array.from(byCapability.values())
    .map((list) => ({ capabilityId: list[0].capabilityId, name: list[0].capabilityName, ratio: capabilityScore(list) }))
    .filter((c) => c.ratio.percent !== null);
  if (scored.length === 0) return null;
  return scored.reduce((strongest, c) =>
    (c.ratio.percent as number) > (strongest.ratio.percent as number) ? c : strongest
  );
};

// ---------------------------------------------------------------------------
// An employee's level on one capability, from what they have done.
//  - Roleplays anchor it: the mean of their 5 most recent Roleplays on the
//    capability (Weak 1, Developing 2, Capable 3, Advanced 4), rounded down.
//  - Quiz Questions can only pull it down: if they passed 40% or fewer of
//    their 5 most recent Quiz Questions on the capability, the level drops
//    one from the anchor. A strong quiz record never raises it.
//  - With fewer than 10 of either, the same rule runs on what there is.
//  - No Roleplay yet means no level: not assessed.
// ---------------------------------------------------------------------------

export const LEVEL_ORDER: CapabilityLevel[] = ['weak', 'developing', 'capable', 'advanced'];
/** How many of the most recent Roleplays, and Quiz Questions, count. */
export const RECENT_COUNT = 5;
/** A quiz pass rate at or below this drops the level by one. */
export const QUIZ_DROP_AT_PERCENT = 40;

export interface RoleplayResult {
  id: string;
  level: CapabilityLevel;
  /** ISO date completed. */
  completedOn: string;
  /** The lesson's name, or "Self Initiated Roleplay". */
  source: string;
  /** Elliott's assessment of the response, where there is one to show. */
  analysis?: string;
  /** The capability's levels the response was graded against. */
  criteria?: Partial<Record<CapabilityLevel, string>>;
}

export interface QuizResult {
  id: string;
  passed: boolean;
  /** ISO date completed. */
  completedOn: string;
  /** The lesson's name. */
  source: string;
}

export interface LevelWorking {
  /** The level after the quiz rule, or null when there is no Roleplay to anchor it. */
  level: CapabilityLevel | null;
  /** The Roleplays that counted, newest first. */
  roleplays: RoleplayResult[];
  /** The Quiz Questions that counted, newest first. */
  quizzes: QuizResult[];
  /** Mean of the counted Roleplays on the 1-4 scale, to one decimal. */
  roleplayMean: number | null;
  /** The level the Roleplays alone give. */
  anchor: CapabilityLevel | null;
  /** Whole percent of the counted Quiz Questions passed, or null with none. */
  quizPercent: number | null;
  /** True when the quiz rule took the level down one. */
  dropped: boolean;
  /**
   * The level comes from Roleplays graded on the capability's earlier levels:
   * they were edited since, and there is no Roleplay on the current ones yet.
   * The level still stands and counts; it is shown flagged, with a prompt to
   * reassess (the backend sets it; absent means current).
   */
  onPreviousLevels?: boolean;
}

const newestFirst = <T extends { completedOn: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => b.completedOn.localeCompare(a.completedOn)).slice(0, RECENT_COUNT);

export const levelFromHistory = (allRoleplays: RoleplayResult[], allQuizzes: QuizResult[]): LevelWorking => {
  const roleplays = newestFirst(allRoleplays);
  const quizzes = newestFirst(allQuizzes);
  const quizPercent = quizzes.length
    ? Math.round((quizzes.filter((q) => q.passed).length / quizzes.length) * 100)
    : null;
  if (roleplays.length === 0) {
    return { level: null, roleplays, quizzes, roleplayMean: null, anchor: null, quizPercent, dropped: false };
  }
  const mean = roleplays.reduce((sum, r) => sum + LEVEL_ORDER.indexOf(r.level) + 1, 0) / roleplays.length;
  const anchorIndex = Math.floor(mean) - 1;
  const dropped = quizPercent !== null && quizPercent <= QUIZ_DROP_AT_PERCENT && anchorIndex > 0;
  return {
    level: LEVEL_ORDER[dropped ? anchorIndex - 1 : anchorIndex],
    roleplays,
    quizzes,
    roleplayMean: Math.round(mean * 10) / 10,
    anchor: LEVEL_ORDER[anchorIndex],
    quizPercent,
    dropped
  };
};
